import { useState, useCallback, useEffect } from "react";
import { Redirect } from "react-router";
import { useTranslation } from "react-i18next";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { useAppDispatch } from "store/hooks";
import PageHelmet from "modules/pageSetting/PageHelmet";
import { updateGlobalUserPreference } from "modules/globalUserPreference";
import { getIsSSOEventLinkSource } from "modules/auth/utils/ssoUtils";
import useQueryParams from "modules/queryParams/hooks/useQueryParams";
import actions from "../redux/actions";
import { UpdateProfilePrompt, UpdateProfileScreen } from "../components";
import { styles } from "./styles";

const TOTAL_STEPS = 2;

const OnboardingPage = (): JSX.Element => {
  const { t } = useTranslation(["onboarding"]);
  const { event } = useQueryParams();
  const eventCode = event ? event.toString() : "";
  const [currentStep, setCurrentStep] = useState(0);
  const dispatch = useAppDispatch();

  const finishOnboarding = useCallback(() => {
    dispatch(actions.showCamAndMicPopover(true));
    updateGlobalUserPreference({ tutorial: { done: true, step: TOTAL_STEPS } });
    setCurrentStep(TOTAL_STEPS);
  }, [dispatch]);

  const goToNextStep = () => {
    const nextStep = currentStep + 1;

    if (nextStep === TOTAL_STEPS) {
      finishOnboarding();
    }
    setCurrentStep(nextStep);
  };

  // skip onboarding and profile updating if login from SSO
  useEffect(() => {
    if (getIsSSOEventLinkSource()) {
      finishOnboarding();
    }
  }, [finishOnboarding]);

  if (getIsSSOEventLinkSource()) {
    return <Redirect to={`/mic-cam-check?event=${eventCode}`} />;
  }

  return (
    <Box sx={styles.root}>
      <PageHelmet title={t("page.title")} />
      {currentStep === 0 && (
        <UpdateProfilePrompt
          goToProfileSetup={goToNextStep}
          skipOnboarding={finishOnboarding}
        />
      )}
      {currentStep === 1 && (
        <UpdateProfileScreen finishOnboarding={goToNextStep} />
      )}
      {currentStep === 2 && (
        <Redirect to={`/mic-cam-check?event=${eventCode}`} />
      )}
    </Box>
  );
};

export default OnboardingPage;
